/**
 * ChannelsPanel Component
 * Displays and allows editing of Demand Gen channel controls for an ad group
 *
 * Per Google Ads API, channel controls are set at the ad group level.
 * Available channels: YouTube In-Stream, YouTube In-Feed, YouTube Shorts,
 * Discover, Gmail, Display (third-party), Maps
 *
 * Changes are queued to the pendingChangesStore and applied when the user
 * clicks Save in the SaveChangesBar.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import type { AdGroup, Campaign } from '@/types/campaign';
import {
  usePendingChangesStore,
  getChannelSettingsChangeKey,
  type ChannelSettings,
  type ChannelSettingsChange,
} from '@/store/pendingChangesStore';

// Simple toggle switch component
interface ToggleSwitchProps {
  checked: boolean;
  onChange: () => void;
  label: string;
  isPending?: boolean;
}

function ToggleSwitch({ checked, onChange, label, isPending }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={`
        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
        ${checked ? 'bg-blue-600' : 'bg-gray-200'}
        ${isPending ? 'ring-2 ring-yellow-400 ring-offset-1' : ''}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
          transition duration-200 ease-in-out
          ${checked ? 'translate-x-5' : 'translate-x-0'}
        `}
      />
    </button>
  );
}

interface ChannelsPanelProps {
  adGroup: AdGroup;
  campaign: Campaign;
}

const DEFAULT_CHANNEL_SETTINGS: ChannelSettings = {
  youtubeInStream: true,
  youtubeInFeed: true,
  youtubeShorts: true,
  discover: true,
  gmail: true,
  display: false, // Third-party disabled by default
  // maps: true,  // Not yet supported in Google Ads API v22/v23
};

const CHANNEL_CONFIG = [
  {
    key: 'youtubeInStream' as const,
    label: 'YouTube In-Stream',
    description: 'Skippable video ads before, during, or after videos',
    group: 'youtube',
  },
  {
    key: 'youtubeInFeed' as const,
    label: 'YouTube In-Feed',
    description: 'Video ads in YouTube search results and home feed',
    group: 'youtube',
  },
  {
    key: 'youtubeShorts' as const,
    label: 'YouTube Shorts',
    description: 'Video ads between Shorts videos',
    group: 'youtube',
  },
  {
    key: 'discover' as const,
    label: 'Discover',
    description: 'Ads in the Google Discover feed',
    group: 'google',
  },
  {
    key: 'gmail' as const,
    label: 'Gmail',
    description: 'Ads in Gmail promotions and social tabs',
    group: 'google',
  },
  // Maps is not yet supported in Google Ads API v22/v23
  // {
  //   key: 'maps' as const,
  //   label: 'Maps',
  //   description: 'Ads on Google Maps',
  //   group: 'google',
  // },
  {
    key: 'display' as const,
    label: 'Display Network',
    description: 'Ads on third-party websites and apps',
    group: 'third-party',
  },
];

export function ChannelsPanel({ adGroup, campaign }: ChannelsPanelProps) {
  const {
    queueChannelSettingsChange,
    getPendingChannelSettingsChange,
    getAppliedChannelSettings,
    registerDiscardCallback,
    unregisterDiscardCallback,
  } = usePendingChangesStore();

  // Get the original settings from the ad group or campaign
  const originalSettings = useMemo<ChannelSettings>(() => {
    // Try to get settings from ad group first
    const agSettings = (adGroup as unknown as { demand_gen_channel_settings?: ChannelSettings })
      .demand_gen_channel_settings;
    if (agSettings) {
      return agSettings;
    }

    // Fall back to campaign defaults
    const campSettings = (campaign as unknown as { demand_gen_settings?: { channels?: ChannelSettings } })
      .demand_gen_settings?.channels;
    if (campSettings) {
      return campSettings;
    }

    return DEFAULT_CHANNEL_SETTINGS;
  }, [adGroup, campaign]);

  // Get ad group identifiers
  const adGroupId = adGroup.ad_group_id || (adGroup as unknown as { ads_group_id: string }).ads_group_id || '';
  const adGroupName = adGroup.ad_group_name || adGroup.ads_group_name || 'Ad Group';
  const campaignId = campaign.campaign_id;
  const customerId = campaign.google_ads_account_id || '';

  // Guard against missing identifiers to prevent key collisions
  const hasValidIdentifiers = Boolean(campaignId && adGroupId && customerId);

  // Check for pending changes and applied settings (saved but not yet synced)
  const pendingChange = hasValidIdentifiers
    ? getPendingChannelSettingsChange(campaignId, adGroupId, customerId)
    : undefined;
  const appliedSettings = hasValidIdentifiers
    ? getAppliedChannelSettings(campaignId, adGroupId, customerId)
    : undefined;

  // Local state for current settings
  // Priority: pending changes > applied settings (session persistence) > original settings
  const [channelSettings, setChannelSettings] = useState<ChannelSettings>(() => {
    return pendingChange?.newSettings || appliedSettings || originalSettings;
  });

  // Update local state when pending changes or applied settings change
  // Priority: pending changes > applied settings (session persistence) > keep current
  useEffect(() => {
    if (pendingChange) {
      setChannelSettings(pendingChange.newSettings);
    } else if (appliedSettings) {
      // After save, pending becomes undefined but applied settings persist for session
      setChannelSettings(appliedSettings);
    }
    // Intentionally NOT resetting to originalSettings when both are undefined
    // - Discard: discardCallback resets to originalSettings
  }, [pendingChange, appliedSettings]);

  // Register discard callback to reset local state when changes are discarded
  // Use the same key helper as the store to avoid drift
  useEffect(() => {
    // Skip registration if no valid customer ID
    if (!customerId || !customerId.replace(/\D/g, '')) {
      return;
    }

    // Use the exported key helper to ensure consistency with the store
    const callbackKey = getChannelSettingsChangeKey(campaignId, adGroupId, customerId);

    const discardCallback = () => {
      setChannelSettings(originalSettings);
    };

    registerDiscardCallback(callbackKey, discardCallback);

    return () => {
      unregisterDiscardCallback(callbackKey);
    };
  }, [campaignId, adGroupId, customerId, originalSettings, registerDiscardCallback, unregisterDiscardCallback]);

  const handleToggle = useCallback(
    (key: keyof ChannelSettings) => {
      // Guard against missing identifiers - should not happen in normal flow
      if (!hasValidIdentifiers) {
        console.error('Cannot toggle channel settings: missing campaign, ad group, or customer ID');
        return;
      }

      // Compute new settings first, then update local state and queue change
      // This avoids calling queueChannelSettingsChange inside setChannelSettings,
      // which would cause a setState during render error
      const updated = { ...channelSettings, [key]: !channelSettings[key] };

      // Update local state
      setChannelSettings(updated);

      // Queue the change to the pending changes store (outside of setState callback)
      const change: ChannelSettingsChange = {
        campaignId,
        adGroupId,
        adGroupName,
        customerId,
        oldSettings: originalSettings,
        newSettings: updated,
      };
      queueChannelSettingsChange(change);
    },
    [channelSettings, campaignId, adGroupId, adGroupName, customerId, originalSettings, queueChannelSettingsChange, hasValidIdentifiers]
  );

  // Check if a specific channel has a pending change
  const hasChannelPendingChange = useCallback(
    (key: keyof ChannelSettings): boolean => {
      if (!pendingChange) return false;
      return pendingChange.oldSettings[key] !== pendingChange.newSettings[key];
    },
    [pendingChange]
  );

  // Count enabled channels
  const enabledCount = Object.values(channelSettings).filter(Boolean).length;
  const totalCount = Object.keys(channelSettings).length;

  // Group channels by category
  const youtubeChannels = CHANNEL_CONFIG.filter((c) => c.group === 'youtube');
  const googleChannels = CHANNEL_CONFIG.filter((c) => c.group === 'google');
  const thirdPartyChannels = CHANNEL_CONFIG.filter((c) => c.group === 'third-party');

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-gray-600">
            Control where your Demand Gen ads appear
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {enabledCount} of {totalCount} channels enabled
            {pendingChange && (
              <span className="ml-2 text-yellow-600 font-medium">
                (unsaved changes)
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* YouTube Channels */}
        <div>
          <h4 className="text-xs font-semibold text-gray-500 tracking-wider mb-3">
            YouTube
          </h4>
          <div className="space-y-3">
            {youtubeChannels.map((channel) => (
              <div
                key={channel.key}
                className={`flex items-start justify-between p-3 rounded-lg ${
                  hasChannelPendingChange(channel.key)
                    ? 'bg-yellow-50 border border-yellow-200'
                    : 'bg-gray-50'
                }`}
              >
                <div className="flex-1 mr-4">
                  <p className="text-sm font-medium text-gray-900">{channel.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{channel.description}</p>
                </div>
                <ToggleSwitch
                  checked={channelSettings[channel.key]}
                  onChange={() => handleToggle(channel.key)}
                  label={`Toggle ${channel.label}`}
                  isPending={hasChannelPendingChange(channel.key)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Google-Owned Channels */}
        <div>
          <h4 className="text-xs font-semibold text-gray-500 tracking-wider mb-3">
            Google Owned Channels
          </h4>
          <div className="space-y-3">
            {googleChannels.map((channel) => (
              <div
                key={channel.key}
                className={`flex items-start justify-between p-3 rounded-lg ${
                  hasChannelPendingChange(channel.key)
                    ? 'bg-yellow-50 border border-yellow-200'
                    : 'bg-gray-50'
                }`}
              >
                <div className="flex-1 mr-4">
                  <p className="text-sm font-medium text-gray-900">{channel.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{channel.description}</p>
                </div>
                <ToggleSwitch
                  checked={channelSettings[channel.key]}
                  onChange={() => handleToggle(channel.key)}
                  label={`Toggle ${channel.label}`}
                  isPending={hasChannelPendingChange(channel.key)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Third-Party Channels */}
        <div>
          <h4 className="text-xs font-semibold text-gray-500 tracking-wider mb-3">
            Third-Party Network
          </h4>
          <div className="space-y-3">
            {thirdPartyChannels.map((channel) => (
              <div
                key={channel.key}
                className={`flex items-start justify-between p-3 rounded-lg ${
                  hasChannelPendingChange(channel.key)
                    ? 'bg-yellow-50 border border-yellow-300'
                    : 'bg-yellow-50 border border-yellow-200'
                }`}
              >
                <div className="flex-1 mr-4">
                  <p className="text-sm font-medium text-gray-900">{channel.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{channel.description}</p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Expands reach beyond Google-owned properties
                  </p>
                </div>
                <ToggleSwitch
                  checked={channelSettings[channel.key]}
                  onChange={() => handleToggle(channel.key)}
                  label={`Toggle ${channel.label}`}
                  isPending={hasChannelPendingChange(channel.key)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-800">
          <strong>Note:</strong> Channel controls affect where your ads can appear. Disabling
          channels may reduce reach but can help focus spend on specific placements.
          {pendingChange && (
            <span className="block mt-1 text-yellow-700">
              Changes will be applied when you click Save in the toolbar above.
            </span>
          )}
        </p>
      </div>
    </div>
  );
}

export default ChannelsPanel;
